import { toast as sonnerToast } from 'sonner';
import { CheckCircle, XCircle, AlertTriangle, Info } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface ToastOptions {
  type: ToastType;
  title: string;
  description?: string;
  duration?: number;
}

const iconMap = {
  success: CheckCircle,
  error: XCircle,
  warning: AlertTriangle,
  info: Info,
};

const iconColorMap = {
  success: 'text-green-600 dark:text-green-400',
  error: 'text-red-600 dark:text-red-400',
  warning: 'text-yellow-600 dark:text-yellow-400',
  info: 'text-blue-600 dark:text-blue-400',
};

function showToast({ type, title, description, duration = 5000 }: ToastOptions) {
  const Icon = iconMap[type];
  const iconColor = iconColorMap[type];

  const toastFn = {
    success: sonnerToast.success,
    error: sonnerToast.error,
    warning: sonnerToast.warning,
    info: sonnerToast.info,
  }[type];

  toastFn(title, {
    description,
    duration,
    icon: <Icon className={`w-5 h-5 ${iconColor}`} />,
  });
}

export function useToast() {
  const addToast = (options: ToastOptions) => {
    showToast(options);
  };

  return {
    addToast,
    toast: {
      success: (title: string, description?: string) =>
        showToast({ type: 'success', title, description }),
      error: (title: string, description?: string) =>
        showToast({ type: 'error', title, description }),
      warning: (title: string, description?: string) =>
        showToast({ type: 'warning', title, description }),
      info: (title: string, description?: string) =>
        showToast({ type: 'info', title, description }),
    },
    dismiss: sonnerToast.dismiss,
  };
}

export { showToast as toast };
